import { Link } from "react-router-dom";
import { ArrowRight, Sprout, Library, Scale, Briefcase, Layers } from "lucide-react";
import { IMG } from "../data/content";
import Seo from "../components/Seo";

const PILLARS = [
  {
    no: "01",
    icon: Sprout,
    title: "Biological Infrastructure",
    items: [
      "Community Seed Banks",
      "Seed Production Areas",
      "Seed Orchards",
      "Germplasm Conservation",
      "Provenance Systems",
      "Genetics and Climate Adaptation",
    ],
  },
  {
    no: "02",
    icon: Library,
    title: "Knowledge Infrastructure",
    items: [
      "Seed Intelligence Platform",
      "Collection Apps",
      "Restoration Readiness Frameworks",
      "Species Information Systems",
      "Training Programs",
      "Knowledge Libraries",
    ],
  },
  {
    no: "03",
    icon: Scale,
    title: "Governance Infrastructure",
    items: [
      "Cultural Authority Frameworks",
      "Permits and Permissions",
      "Indigenous Data Sovereignty",
      "Seed Use Agreements",
      "Benefit-Sharing Frameworks",
    ],
  },
  {
    no: "04",
    icon: Briefcase,
    title: "Enterprise Infrastructure",
    items: [
      "Indigenous Seed Enterprises",
      "Ranger Pathways",
      "Workforce Development",
      "Market Readiness",
      "Commercial Partnerships",
    ],
  },
  {
    no: "05",
    icon: Layers,
    title: "Restoration Infrastructure",
    items: [
      "Seed Supply Planning",
      "Restoration Readiness Assessments",
      "Regional Activation Strategies",
      "Stewardship Frameworks",
      "Monitoring Systems",
    ],
  },
];

export default function Infrastructure() {
  return (
    <div data-testid="page-infrastructure">
      <Seo
        title="Restoration Infrastructure — Biological, Knowledge, Governance, Enterprise"
        description="SeedKeepers is a restoration infrastructure organisation. We help communities, governments, industry and Traditional Owners build the systems that make restoration possible."
        path="/infrastructure"
      />

      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="specimen-label mb-6">Restoration Infrastructure</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Seed. <em className="text-eucalypt">Knowledge.</em> Stewardship.
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              We help communities, governments, industry and Traditional
              Owners build the systems that make restoration possible.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Our work spans community seed banks, seed production,
              provenance, genetics, Indigenous enterprise, data systems and
              long-term stewardship.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src={IMG.containerSeedBank} alt="Community seed bank interior" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Five Pillars */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">The Five Pillars</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3 max-w-3xl leading-[1.02]">
            Five layers of infrastructure. <em className="text-eucalypt">One restoration system.</em>
          </h2>
          <p className="text-muted max-w-2xl mb-14 text-sm md:text-base leading-relaxed">
            Each pillar carries weight on its own. Together they create the
            capability Australia needs to deliver restoration at scale and on
            Country.
          </p>

          <div className="space-y-px bg-rule border border-rule" data-testid="pillars">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.no} className="bg-card p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-testid={`pillar-${p.no}`}>
                  <div className="lg:col-span-4">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="font-serif text-6xl md:text-7xl text-ochre2 leading-none">{p.no}</span>
                      <Icon size={28} className="text-eucalypt" />
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl leading-tight">{p.title}</h3>
                  </div>
                  <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                    {p.items.map((it, j) => (
                      <li key={it} className="flex gap-4 items-baseline border-b border-rule pb-2" data-testid={`pillar-${p.no}-item-${j}`}>
                        <span className="font-mono text-[10px] tracking-[0.2em] text-muted">{String(j + 1).padStart(2, "0")}</span>
                        <span className="font-serif text-lg md:text-xl leading-tight">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">The Position</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            Projects can restore a site.
            <br />
            <span className="italic text-eucalypt">Systems can restore a landscape.</span>
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="infrastructure-contact-cta"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Build infrastructure with us <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
